import { Platform, StyleSheet } from 'react-native';
import { pick } from 'lodash';
import { useSelector } from 'react-redux';

import { appInfoSelector } from '@common/redux/selectors/app';
import { getShadow } from '../func';
import { IRectangle } from '@common/types/element';
import { AppVersion } from 'click-types';

const createStyles = (attributes: IRectangle) => {
  const appInfo = useSelector(appInfoSelector);

  // Commonly used, well-named locals for readability
  const style = attributes.style || {};
  const isAndroid = Platform.OS === 'android';
  const isWeb = Platform.OS === 'web';
  const isV4 = appInfo?.version === AppVersion.V4;
  let {
    backgroundColor,
    backgroundStyle,
    borderRadius,
    borderStyle,
    borderWidth,
    borderColor,
    opacity,
    width,
  } = attributes;

  // Maintain existing behavior of overriding borderRadius for V4
  if (isV4) {
    if (
      typeof backgroundStyle === 'object' &&
      backgroundStyle.type === 'color'
    ) {
      backgroundColor = style.backgroundColor;
    }
    borderRadius = style?.borderRadius;
    borderStyle = style?.borderStyle;
    borderWidth = style?.borderWidth;
    borderColor = style?.borderColor;
    opacity = style?.opacity;
    width = style?.width;
  }

  return StyleSheet.create({
    container: {
      // Basic box and z-index
      ...getShadow(attributes),
      ...pick(attributes, ['zIndex']),

      // List context
      ...(attributes?.isInCustomList
        ? { paddingBottom: attributes.paddingBottom }
        : {}),

      // Sizing
      minHeight: attributes.height,

      // Radius: handle Android zero-radius quirk and origin scaling
      ...(isAndroid && borderRadius === 0
        ? { borderRadius: 1 }
        : { borderRadius: borderRadius * attributes.originX }),

      // Final override according to app version rules
      ...(isV4
        ? pick(style, [
            'top',
            'left',
            'bottom',
            'right',
            'position',
            'transform',
            'margin',
            'marginTop',
            'marginRight',
            'marginBottom',
            'marginLeft',
          ])
        : pick(attributes, ['marginTop', 'marginLeft'])),
      borderRadius,
      backgroundColor,
      opacity,
      width,

      // Platform-specific border application rules
      ...(isWeb
        ? {
            ...(borderStyle !== 'none' && {
              borderStyle,
              borderWidth,
              borderColor,
            }),
          }
        : {
            ...(borderStyle !== 'none'
              ? {
                  borderWidth,
                  borderColor,
                }
              : {}),
            borderStyle: borderStyle === 'none' ? 'solid' : borderStyle,
          }),
    },
  });
};

export default createStyles;
